import * as atlas from 'azure-maps-control';
import React, { useEffect, useRef, useState } from 'react';
import '../../../style/Map.scss';
import { Marker, MarkerLayer, ClusterLayer } from '.';
import { MarkerUtil } from './markers/Marker.util';
import { createPortal } from 'react-dom';

enum AtlastMapSource {
  datasource = 'datasource',
}

interface VentoryMapProps<M extends Marker> {
  markers: Map<string, M>;
  markerLayer: MarkerLayer<M>;
  clusterLayer?: ClusterLayer<M>;
  onMapReady?: (map: atlas.Map) => void;
}

export default function VentoryMap<M extends Marker>({
  markers,
  markerLayer,
  clusterLayer,
  onMapReady,
}: VentoryMapProps<M>) {
  const [map, setMap] = useState<atlas.Map>();

  const [popupState, setPopupState] = useState<{ element: React.ReactNode; position: atlas.data.Position } | null>(
    null,
  );
  // Container for the portal for the popup
  const popupContentRef = useRef<HTMLDivElement | null>(null);
  if (!popupContentRef.current) {
    popupContentRef.current = document.createElement('div');
  }
  const popupRef = useRef(
    new atlas.Popup({
      pixelOffset: [0, -40],
      closeButton: false,
    }),
  );

  const loadedRef = useRef(false);

  useEffect(() => {
    handleMarkersChanged();
  }, [markers]);

  const handleMarkersChanged = () => {
    if (!map) return;

    const dataSource = map.sources.getById(AtlastMapSource.datasource) as atlas.source.DataSource;
    if (!dataSource) return;

    dataSource.clear();
    for (const marker of [...markers.values()]) {
      const mapFeature = marker.createMapFeature();
      if (!mapFeature) continue;
      dataSource.add(mapFeature);
    }
  };

  const createMapSprites = async (map: atlas.Map) => {
    if (!map.imageSprite) return;
    map.imageSprite.clear();

    if (!map.imageSprite.hasImage('marker-truck')) {
      await map.imageSprite.add(
        'marker-truck',
        `<svg id="euPi9pRCeqX1" width="28" height="28" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32"><ellipse rx="16" ry="16" transform="translate(16 16)" stroke-width="0"/><path d="M12.8,6.625h2.10357c.22005,0,.33012,0,.43371.024176.09189.021438.17964.056788.26019.104764.09081.05411.16866.12978.32427.281111l3.65652,3.554924c.15561.151288.23346.226975.28908.315263.04941.078312.08577.163625.10782.252962C20,11.258913,20,11.365925,20,11.579862v2.482638c0,.407662,0,.611537-.06849.772363-.09135.214375-.26658.384737-.48708.47355-.16542.066588-.37512.066588-.79443.066588m-4.5,0h-1.35m0,0v-8.575c0-.980087,0-1.47014-.1962-1.844482-.17253-.329289-.44793-.597004-.7866-.764776C11.43218,4,10.92809,4,9.92,4h-5.04c-1.00809,0-1.512144,0-1.897182.190741-.338697.167773-.614061.435488-.786627.764776C2,5.32986,2,5.819913,2,6.8v6.825c0,.966525.805887,1.75,1.8,1.75m9,0h-3.6m0,0C9.2,16.824788,7.991165,18,6.5,18s-2.7-1.175213-2.7-2.625m5.4,0c0-1.449787-1.208835-2.625-2.7-2.625s-2.7,1.175213-2.7,2.625m14.85.4375C18.65,17.020613,17.64263,18,16.4,18s-2.25-.979387-2.25-2.1875s1.00737-2.1875,2.25-2.1875s2.25.979387,2.25,2.1875Z" transform="translate(5 5)" fill="none" stroke="#FFFFFF" stroke-width="1.667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      );
    }

    if (!map.imageSprite.hasImage('marker-truck-active')) {
      await map.imageSprite.add(
        'marker-truck-active',
        `<svg id="euPi9pRCeqX1" width="28" height="28" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32"><ellipse rx="16" ry="16" transform="translate(16 16)" stroke-width="0" fill="#027A48"/><path d="M12.8,6.625h2.10357c.22005,0,.33012,0,.43371.024176.09189.021438.17964.056788.26019.104764.09081.05411.16866.12978.32427.281111l3.65652,3.554924c.15561.151288.23346.226975.28908.315263.04941.078312.08577.163625.10782.252962C20,11.258913,20,11.365925,20,11.579862v2.482638c0,.407662,0,.611537-.06849.772363-.09135.214375-.26658.384737-.48708.47355-.16542.066588-.37512.066588-.79443.066588m-4.5,0h-1.35m0,0v-8.575c0-.980087,0-1.47014-.1962-1.844482-.17253-.329289-.44793-.597004-.7866-.764776C11.43218,4,10.92809,4,9.92,4h-5.04c-1.00809,0-1.512144,0-1.897182.190741-.338697.167773-.614061.435488-.786627.764776C2,5.32986,2,5.819913,2,6.8v6.825c0,.966525.805887,1.75,1.8,1.75m9,0h-3.6m0,0C9.2,16.824788,7.991165,18,6.5,18s-2.7-1.175213-2.7-2.625m5.4,0c0-1.449787-1.208835-2.625-2.7-2.625s-2.7,1.175213-2.7,2.625m14.85.4375C18.65,17.020613,17.64263,18,16.4,18s-2.25-.979387-2.25-2.1875s1.00737-2.1875,2.25-2.1875s2.25.979387,2.25,2.1875Z" transform="translate(5 5)" fill="none" stroke="#FFFFFF" stroke-width="1.667" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      );
    }

    if (!map.imageSprite.hasImage('marker-building')) {
      await map.imageSprite.add(
        'marker-building',
        `<svg id="eFCDObmio6L1" width="28" height="28" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><ellipse rx="16" ry="16" transform="translate(16 16)" stroke-width="0"/><path d="M6.4,6.555556h2.2M6.4,10.111111h2.2M6.4,13.666667h2.2m2.8-7.111111h2.2m-2.2,3.555555h2.2m-2.2,3.555556h2.2M16.4,19v-13.155556c0-.995644,0-1.493475-.1744-1.87376-.15336-.334515-.39816-.60648-.6992-.776915C15.18416,3,14.73608,3,13.84,3h-7.68c-.896088,0-1.344128,0-1.686384.193769-.301064.170435-.545832.4424-.699224.776915C3.6,4.350969,3.6,4.8488,3.6,5.844444L3.6,19M18,19h-16" transform="translate(6 5)" fill="none" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
      );
    }

    if (!map.imageSprite.hasImage('marker-orange')) {
      await map.imageSprite.createFromTemplate('marker-orange', 'marker', 'orange');
    }
    if (!map.imageSprite.hasImage('marker-red')) {
      await map.imageSprite.createFromTemplate('marker-red', 'marker', 'red');
    }
    if (!map.imageSprite.hasImage('marker-yellow')) {
      await map.imageSprite.createFromTemplate('marker-yellow', 'marker', 'yellow');
    }
    if (!map.imageSprite.hasImage('marker-green')) {
      await map.imageSprite.createFromTemplate('marker-green', 'marker', 'green');
    }
  };

  const dataSourceRef = useRef(
    new atlas.source.DataSource(AtlastMapSource.datasource, {
      cluster: !!clusterLayer,
      clusterRadius: 60,
      clusterProperties: clusterLayer?.clusterProperties,
    }),
  );
  const dataSource = dataSourceRef.current;

  // Sync popup content with Azure Maps when state changes.
  useEffect(() => {
    if (!map) return;

    const popup = popupRef.current;
    if (popupState) {
      popup.setOptions({
        content: popupContentRef.current!,
        position: popupState.position,
      });
      popup.open(map);
    } else {
      popup.remove();
    }
  }, [popupState, map]);

  const initializeMap = async () => {
    if (!map) return;

    createMapSprites(map);
    map.setCamera(MarkerUtil.getInitialMapState([...markers.values()]));

    map.layers.clear();
    map.sources.clear();

    const clusterSymbolLayer = clusterLayer && new atlas.layer.SymbolLayer(dataSource, undefined, clusterLayer.options);
    const symbolLayer = new atlas.layer.SymbolLayer(dataSource, undefined, markerLayer.options);

    map.events.add('stylechanged', () => {
      map.sources.add(dataSource);
      map.layers.add(symbolLayer);
      if (clusterSymbolLayer) map.layers.add(clusterSymbolLayer);
    });

    map.events.add('ready', () => {
      for (const marker of [...markers.values()]) {
        const mapFeature = marker.createMapFeature();
        if (!mapFeature) continue;
        dataSource.add(mapFeature);
      }

      if (markerLayer.popupBuilder) {
        map.events.add('mouseenter', symbolLayer, e => {
          map.getCanvasContainer().style.cursor = 'pointer';
          const shape = e.shapes && e.shapes[0];
          if (!shape || !(shape instanceof atlas.Shape)) return;

          const properties = shape.getProperties();
          const position = shape.getCoordinates() as atlas.data.Position;

          const marker = markers.get(properties.id);
          if (!marker) return;

          const element = markerLayer.popupBuilder!(marker);
          setPopupState({ element, position });
        });

        map.events.add('mouseleave', symbolLayer, () => {
          setPopupState(null);
          map.getCanvasContainer().style.cursor = 'grab';
        });
      }

      if (clusterLayer && clusterLayer.popupBuilder && clusterSymbolLayer) {
        map.events.add('mouseenter', clusterSymbolLayer, e => {
          const cluster = e.shapes && e.shapes[0];
          if (!cluster || cluster instanceof atlas.Shape) return;

          const properties = cluster.properties;
          const position = cluster.geometry.coordinates as atlas.data.Position;

          const markerIds = properties.ids.split('::') as string[];
          markerIds.pop();
          const markersInCluster = markerIds.map(id => markers.get(id)).filter(m => !!m) as M[];

          const element = clusterLayer.popupBuilder!(properties, markersInCluster);
          setPopupState({ element, position });
        });

        map.events.add('mouseleave', clusterSymbolLayer, () => {
          setPopupState(null);
          map.getCanvasContainer().style.cursor = 'grab';
        });
      }

      onMapReady?.(map);
    });
  };

  useEffect(() => {
    initializeMap();
  }, [map]);

  const createMap = () => {
    if (loadedRef.current) return;
    setMap(
      new atlas.Map('map', {
        center: [0, -100],
        maxZoom: 18,
        minZoom: 1,
        zoom: 8,
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey: process.env.REACT_APP_AZURE_MAPS_SUBSCRIPTION_KEY || 'no_key',
        },
      }),
    );
    loadedRef.current = true;
  };

  useEffect(() => {
    createMap();
    return () => {
      popupRef.current.remove();
      map?.dispose();
    };
  }, []);

  return (
    <div
      id='map'
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '4px',
      }}
    >
      {popupState && popupContentRef.current && createPortal(popupState.element, popupContentRef.current)}
    </div>
  );
}
